import { useCallback, useEffect, useRef } from "react";

const IDLE_EVENTS = ["mousemove", "keydown", "mousedown", "touchstart", "scroll", "click"] as const;
/** Moments the page can resume after timers were frozen (locked phone,
 *  backgrounded app, sleeping laptop) — checked against the wall clock. */
const RESUME_EVENTS = ["focus", "pageshow"] as const;

interface Options {
  timeoutMs: number;
  onIdle: () => void;
  onWarn?: () => void;
  onActive?: () => void;
  warnBeforeMs?: number;
  enabled?: boolean;
}

export function useIdleTimer({
  timeoutMs,
  onIdle,
  onWarn,
  onActive,
  warnBeforeMs = 60_000,
  enabled = true,
}: Options) {
  const idleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnedRef = useRef(false);
  // Wall-clock time of the last activity. setTimeout alone is not enough:
  // mobile browsers freeze timers while the screen is locked or the app is
  // backgrounded, so the first touch on return used to reset() the overdue
  // timer before it fired and the session silently outlived its timeout.
  const lastActiveRef = useRef(Date.now());

  const clearTimers = () => {
    if (idleRef.current) clearTimeout(idleRef.current);
    if (warnRef.current) clearTimeout(warnRef.current);
  };

  const expire = useCallback(() => {
    if (idleRef.current) clearTimeout(idleRef.current);
    if (warnRef.current) clearTimeout(warnRef.current);
    onIdle();
  }, [onIdle]);

  const reset = useCallback(() => {
    if (Date.now() - lastActiveRef.current >= timeoutMs) {
      expire();
      return;
    }
    lastActiveRef.current = Date.now();
    if (idleRef.current) clearTimeout(idleRef.current);
    if (warnRef.current) clearTimeout(warnRef.current);

    if (warnedRef.current) {
      warnedRef.current = false;
      onActive?.();
    }

    if (onWarn) {
      warnRef.current = setTimeout(() => {
        warnedRef.current = true;
        onWarn();
      }, timeoutMs - warnBeforeMs);
    }

    idleRef.current = setTimeout(onIdle, timeoutMs);
  }, [timeoutMs, onIdle, onWarn, onActive, warnBeforeMs, expire]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }
    // A fresh session (sign-in) starts its clock now, not from the last one.
    lastActiveRef.current = Date.now();
    reset();
    // Returning to the page is not activity — only expire if overdue.
    const checkOverdue = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastActiveRef.current >= timeoutMs) expire();
    };
    for (const ev of IDLE_EVENTS) window.addEventListener(ev, reset, { passive: true });
    for (const ev of RESUME_EVENTS) window.addEventListener(ev, checkOverdue);
    document.addEventListener("visibilitychange", checkOverdue);
    return () => {
      clearTimers();
      for (const ev of IDLE_EVENTS) window.removeEventListener(ev, reset);
      for (const ev of RESUME_EVENTS) window.removeEventListener(ev, checkOverdue);
      document.removeEventListener("visibilitychange", checkOverdue);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reset, expire, timeoutMs]);
}
